import { TDocumentType } from '@/@types/document-type';
import { api } from '@/services/apiClient';
import { useQuery } from '@tanstack/react-query';

interface GetDocumentTypeProps {
  page?: number;
  perPage?: number;
  search?: string;
  sortBy?: string;
  sort?: string;
  deleted?: boolean;
  enabled?: boolean;
}

type GetDocumentTypeResponse = {
  documentTypes: TDocumentType[];
  totalCount: number;
};

export async function getDocumentTypes({
  page,
  perPage,
  search,
  sortBy,
  sort,
  deleted,
}: GetDocumentTypeProps): Promise<GetDocumentTypeResponse> {
  const { data } = await api.get('/document_types', {
    params: {
      page,
      per_page: perPage,
      search,
      sort_by: sortBy,
      sort: sort ? sort.toUpperCase() : undefined,
      deleted,
    },
  });

  const totalCount = data.total;

  const documentTypes = data.content.map((documentType: any) => ({
    id: documentType.id,
    name: documentType.name,
    type: documentType.type,
    affectsCashMovement: documentType.affects_cash_movement,
    createdAt: new Date(documentType.created_at).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }),
    deletedAt:
      documentType.deleted_at &&
      new Date(documentType.deleted_at).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }),
  }));

  return { documentTypes, totalCount };
}

export function useDocumentTypes({
  page = 0,
  perPage = 0,
  deleted,
  search,
  sortBy,
  sort,
  enabled = true,
}: GetDocumentTypeProps) {
  return useQuery({
    queryKey: [
      'documentTypes',
      { page, perPage, deleted, search, sortBy, sort },
    ],
    queryFn: () =>
      getDocumentTypes({ page, perPage, deleted, search, sortBy, sort }),
    staleTime: 1000 * 60 * 1, // 1 minute
    enabled,
  });
}
